import { Archive, X } from 'lucide-react';
import { useBulkUpdateLeads } from '../../hooks/useLeads';
import { usePeople } from '../../hooks/usePeople';
import type { LeadTemperature } from '../../lib/leadsApi';
import { TEMPERATURE_OPTIONS } from './leadConstants';
import { Select } from '../ui/Select';
import { useConfirm } from '../ui/ConfirmProvider';

interface LeadBulkActionsBarProps {
  selectedIds: string[];
  onClear: () => void;
}

/**
 * Floating toolbar shown when one or more leads are selected (CLE-191).
 * Supports bulk assign, temperature, and archive. Stage moves are done by
 * dragging on the board, where the destination stage is unambiguous.
 */
export function LeadBulkActionsBar({ selectedIds, onClear }: LeadBulkActionsBarProps) {
  const bulk = useBulkUpdateLeads();
  const { data: people } = usePeople({ active: 'true' });
  const confirm = useConfirm();

  if (selectedIds.length === 0) return null;

  const assignable = (people ?? []).filter((p) => p.userId);

  function run(action: Parameters<typeof bulk.mutate>[0]['action'], value?: unknown) {
    bulk.mutate({ ids: selectedIds, action, value }, { onSuccess: onClear });
  }

  return (
    <div className="fixed inset-x-0 bottom-4 z-30 mx-auto flex w-full max-w-2xl flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 shadow-lg sm:px-4">
      <span className="text-sm font-medium text-gray-700">{selectedIds.length} selectate</span>

      {/* Action pickers: value stays '' — picking an option runs the bulk action. */}
      <Select
        label="Setează responsabil"
        hideLabel
        size="sm"
        value=""
        onChange={(v) => {
          if (v) run('assign', v === '__unassign' ? null : v);
        }}
        placeholder="Setează responsabil…"
        options={[
          { value: '__unassign', label: 'Elimină responsabil' },
          ...assignable.map((p) => ({ value: p.userId as string, label: p.fullName })),
        ]}
        className="w-48"
      />

      <Select
        label="Setează temperatură"
        hideLabel
        size="sm"
        value=""
        onChange={(v) => {
          if (v) run('temperature', v as LeadTemperature);
        }}
        placeholder="Setează temperatură…"
        options={TEMPERATURE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        className="w-48"
      />

      <button
        type="button"
        onClick={async () => {
          if (await confirm({ message: `Arhivezi ${selectedIds.length} lead(uri)?`, danger: true }))
            run('archive');
        }}
        className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-1.5 text-sm text-red-700 hover:bg-red-100"
      >
        <Archive size={14} /> Arhivează
      </button>

      <button
        type="button"
        onClick={onClear}
        className="ml-auto inline-flex items-center gap-1 rounded px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100"
      >
        <X size={14} /> Golește
      </button>

      {bulk.isError && <p className="w-full text-xs text-red-600">Acțiunea în masă a eșuat.</p>}
    </div>
  );
}
